import { Lock, MapPin, Monitor, RotateCcw, ClipboardCheck } from 'lucide-react';

// =================== Sample data ===================
const samples = [
  { id: 's1', paciente_nome: 'Beatriz Mendes Oliveira', hora_inicio: '09:00', hora_fim: '09:30', categoria: 'Consulta Clínica', status: 'confirmada', modo_atendimento: 'presencial', tipo_consulta: 'normal', preventivo: 'sim', valor_total: 600, total_pago: 320 },
  { id: 's2', paciente_nome: 'Carla Souza Lima', hora_inicio: '10:00', hora_fim: '10:30', categoria: 'Reposição Hormonal', status: 'agendada', modo_atendimento: 'online', tipo_consulta: 'normal', valor_total: 250, total_pago: 0 },
  { id: 's3', paciente_nome: 'Helena Martins Barbosa', hora_inicio: '11:00', hora_fim: '11:20', categoria: 'Consulta Ginecológica', status: 'confirmada', modo_atendimento: 'presencial', tipo_consulta: 'retorno', valor_total: 0, total_pago: 0 },
  { id: 's4', paciente_nome: 'Raphael Zelli', hora_inicio: '14:00', hora_fim: '14:30', categoria: 'Consulta Clínica', status: 'realizada', modo_atendimento: 'presencial', valor_total: 400, total_pago: 400 },
  { id: 'b1', paciente_nome: 'Horário Bloqueado', categoria: 'Horário Bloqueado', hora_inicio: '12:00', hora_fim: '13:00' },
];

const STATUS_LABELS = { agendada: 'Não Confirmada', confirmada: 'Confirmada', realizada: 'Realizada', cancelada: 'Cancelada', falta: 'Falta' };

const formatBRL = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const computeFin = (c) => {
  const isBlocked = c.categoria === 'Horário Bloqueado';
  const isCancelledOrMissed = c.status === 'cancelada' || c.status === 'falta';
  const isRetorno = c.tipo_consulta === 'retorno';
  const valorTotal = c.valor_total || 0;
  const pago = c.total_pago || 0;
  const showFin = !isBlocked && !isCancelledOrMissed && !(isRetorno && pago === 0);
  let tone = 'gray';
  if (pago === 0) tone = 'red';
  else if (valorTotal > 0 && pago >= valorTotal) tone = 'green';
  else tone = 'amber';
  return { showFin, tone, pago, valorTotal };
};

const TYPE_OF = (c) => {
  if (c.categoria === 'Horário Bloqueado') return 'blocked';
  if (c.tipo_consulta === 'retorno') return 'retorno';
  if (c.modo_atendimento === 'online') return 'online';
  return 'presencial';
};

// =================== MOCKUP A — Editorial / Serif Time ===================
// Inspirado em Calendly + revistas: tipografia forte, espaçamento generoso, hora em destaque serif
const CardA = ({ c }) => {
  const t = TYPE_OF(c);
  const fin = computeFin(c);
  const isBlocked = t === 'blocked';

  const tColor = {
    presencial: { dot: '#3B82F6', tag: 'text-blue-700' },
    online: { dot: '#6366F1', tag: 'text-indigo-700' },
    retorno: { dot: '#F43F5E', tag: 'text-rose-700' },
    blocked: { dot: '#9CA3AF', tag: 'text-gray-500' },
  }[t];

  const statusDot = { agendada: '#F59E0B', confirmada: '#10B981', realizada: '#6B7280', cancelada: '#EF4444', falta: '#F97316' }[c.status] || '#9CA3AF';
  const finText = { red: 'text-red-500', amber: 'text-amber-600', green: 'text-emerald-600', gray: 'text-gray-300' }[fin.tone];

  if (isBlocked) {
    return (
      <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="font-serif text-2xl font-light text-gray-300 tracking-tight">{c.hora_inicio}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mt-2 flex items-center gap-1.5">
          <Lock className="w-3 h-3" />
          Bloqueado
        </div>
      </div>
    );
  }

  return (
    <div className="group bg-white rounded-2xl p-4 border border-gray-100 hover:border-gray-200 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-all cursor-pointer">
      {/* Time block */}
      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-serif text-2xl font-light text-gray-900 tabular-nums tracking-tight leading-none">{c.hora_inicio}</span>
        <span className="text-[10px] text-gray-300 tracking-wider">— {c.hora_fim}</span>
      </div>

      {/* Type microbar */}
      <div className="flex items-center gap-1.5 mb-2.5">
        <span className="w-1 h-1 rounded-full" style={{ backgroundColor: tColor.dot }}></span>
        <span className={`text-[9px] uppercase tracking-[0.18em] font-semibold ${tColor.tag}`}>
          {t === 'retorno' ? 'Retorno' : t === 'online' ? 'Online' : 'Presencial'}
        </span>
      </div>

      {/* Patient */}
      <div className="text-[14px] font-medium text-gray-900 leading-snug truncate">{c.paciente_nome}</div>
      <div className="text-[11px] text-gray-400 mt-0.5 truncate">{c.categoria}</div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusDot }}></span>
          <span className="text-[10px] font-medium text-gray-500">{STATUS_LABELS[c.status]}</span>
          {c.preventivo === 'sim' && <span className="text-[9px] font-bold text-amber-600 ml-1">PREV</span>}
        </div>
        {fin.showFin && <span className={`text-[11px] font-semibold tabular-nums ${finText}`}>{formatBRL(fin.pago)}</span>}
      </div>
    </div>
  );
};

// =================== MOCKUP B — Premium Glass / Linear-inspired ===================
// Inspirado no Linear/Notion: cards com gradiente sutil, sombra suave, status como vertical strip
const CardB = ({ c }) => {
  const t = TYPE_OF(c);
  const fin = computeFin(c);
  const isBlocked = t === 'blocked';

  const grads = {
    presencial: 'from-blue-50/40 via-white to-white',
    online: 'from-indigo-50/40 via-white to-white',
    retorno: 'from-rose-50/40 via-white to-white',
    blocked: 'from-gray-50 via-white to-white',
  }[t];

  const stripBg = {
    presencial: 'bg-gradient-to-b from-blue-400 to-blue-500',
    online: 'bg-gradient-to-b from-indigo-400 to-indigo-500',
    retorno: 'bg-gradient-to-b from-rose-400 to-rose-500',
    blocked: 'bg-gradient-to-b from-gray-300 to-gray-400',
  }[t];

  const statusBadge = {
    agendada: 'bg-amber-50 text-amber-700 ring-amber-200',
    confirmada: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    realizada: 'bg-gray-50 text-gray-500 ring-gray-200',
    cancelada: 'bg-red-50 text-red-700 ring-red-200',
    falta: 'bg-orange-50 text-orange-700 ring-orange-200',
  }[c.status] || 'bg-gray-50 text-gray-500 ring-gray-200';

  const finText = { red: 'text-red-500', amber: 'text-amber-600', green: 'text-emerald-600', gray: 'text-gray-300' }[fin.tone];

  return (
    <div className={`relative bg-gradient-to-br ${grads} rounded-xl border border-gray-200/70 shadow-[0_1px_3px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)] hover:-translate-y-px transition-all cursor-pointer overflow-hidden`}>
      {/* Vertical color strip */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${stripBg}`}></div>

      {isBlocked ? (
        <div className="pl-5 pr-3 py-3">
          <div className="flex items-center gap-1.5 text-gray-500">
            <Lock className="w-3 h-3" />
            <span className="text-[11px] font-semibold">Horário Bloqueado</span>
          </div>
          <div className="text-[10px] text-gray-400 mt-1 tabular-nums">{c.hora_inicio} – {c.hora_fim}</div>
        </div>
      ) : (
        <div className="pl-5 pr-3 py-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <div className="text-[11px] tabular-nums font-semibold text-gray-700 leading-none">{c.hora_inicio}<span className="text-gray-300 font-normal"> – {c.hora_fim}</span></div>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold ring-1 ${statusBadge}`}>{STATUS_LABELS[c.status]}</span>
          </div>

          {/* Patient + categoria */}
          <div className="text-[13px] font-semibold text-gray-900 truncate leading-tight">{c.paciente_nome}</div>
          <div className="text-[10px] text-gray-500 truncate mt-0.5">{c.categoria}</div>

          {/* Tags row */}
          <div className="flex items-center justify-between mt-2.5">
            <div className="flex items-center gap-1 text-[10px] text-gray-500 font-medium">
              {t === 'online' ? <Monitor className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}
              {t === 'online' ? 'Online' : 'Presencial'}
              {c.tipo_consulta === 'retorno' && (
                <>
                  <span className="text-gray-300">·</span>
                  <RotateCcw className="w-2.5 h-2.5" />
                  Retorno
                </>
              )}
              {c.preventivo === 'sim' && (
                <>
                  <span className="text-gray-300">·</span>
                  <ClipboardCheck className="w-2.5 h-2.5" />
                  Prev
                </>
              )}
            </div>
            {fin.showFin && <span className={`text-[10px] font-bold tabular-nums ${finText}`}>{formatBRL(fin.pago)}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// =================== MOCKUP C — Boutique / Soft Pastel Capsule ===================
// Inspirado em apps de luxo/spa: pílulas suaves, fundo creme, cor como aura ao redor
const CardC = ({ c }) => {
  const t = TYPE_OF(c);
  const fin = computeFin(c);
  const isBlocked = t === 'blocked';

  const auras = {
    presencial: 'bg-blue-50/70 ring-blue-100/80',
    online: 'bg-indigo-50/70 ring-indigo-100/80',
    retorno: 'bg-rose-50/70 ring-rose-100/80',
    blocked: 'bg-stone-100 ring-stone-200',
  }[t];

  const labelTone = {
    presencial: 'text-blue-700 bg-white/60',
    online: 'text-indigo-700 bg-white/60',
    retorno: 'text-rose-700 bg-white/60',
    blocked: 'text-stone-500 bg-white/60',
  }[t];

  const statusDot = { agendada: '#D97706', confirmada: '#059669', realizada: '#6B7280', cancelada: '#DC2626', falta: '#EA580C' }[c.status] || '#9CA3AF';
  const finBg = { red: 'bg-red-100/60 text-red-700', amber: 'bg-amber-100/70 text-amber-800', green: 'bg-emerald-100/60 text-emerald-700', gray: 'bg-gray-100 text-gray-400' }[fin.tone];

  if (isBlocked) {
    return (
      <div className={`${auras} ring-1 rounded-2xl p-3.5`}>
        <div className="flex items-center gap-1.5 text-stone-500">
          <Lock className="w-3 h-3" />
          <span className="text-[11px] font-semibold tracking-tight">Bloqueado</span>
        </div>
        <div className="text-[10px] text-stone-400 mt-1 tabular-nums tracking-wide">{c.hora_inicio} → {c.hora_fim}</div>
      </div>
    );
  }

  return (
    <div className={`${auras} ring-1 rounded-2xl p-3.5 hover:ring-2 transition-all cursor-pointer relative`}>
      {/* Top: time pill + status */}
      <div className="flex items-center justify-between mb-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/80 backdrop-blur-sm text-[10px] font-bold text-gray-700 tabular-nums tracking-wider shadow-sm">
          {c.hora_inicio}
        </span>
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusDot }}></span>
          <span className="text-[9px] uppercase tracking-wider font-semibold text-gray-600">{STATUS_LABELS[c.status]}</span>
        </div>
      </div>

      {/* Patient */}
      <div className="text-[13px] font-semibold text-gray-900 truncate leading-tight">{c.paciente_nome}</div>
      <div className="text-[10px] text-gray-500 italic truncate mt-0.5">{c.categoria}</div>

      {/* Tags as pills */}
      <div className="flex flex-wrap gap-1 mt-2.5">
        <span className={`inline-flex items-center px-1.5 py-px rounded-full text-[9px] font-bold ${labelTone} backdrop-blur-sm`}>
          {t === 'retorno' ? 'Retorno' : t === 'online' ? 'Online' : 'Presencial'}
        </span>
        {c.preventivo === 'sim' && (
          <span className="inline-flex items-center px-1.5 py-px rounded-full text-[9px] font-bold bg-white/60 text-amber-700 backdrop-blur-sm">
            Preventivo
          </span>
        )}
      </div>

      {/* Fin badge floating */}
      {fin.showFin && (
        <span className={`absolute -top-1.5 -right-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold tabular-nums shadow-sm ${finBg} backdrop-blur-sm`}>
          {formatBRL(fin.pago)}
        </span>
      )}
    </div>
  );
};

// =================== PAGE ===================
export default function CardMockupsPage() {
  const SectionTitle = ({ n, name, desc }) => (
    <div className="mb-5">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold">{n}</div>
        <h2 className="text-xl font-bold text-slate-900">{name}</h2>
      </div>
      <p className="text-sm text-slate-500 mt-1.5 ml-11">{desc}</p>
    </div>
  );

  const Grid = ({ Card, bg = 'bg-slate-50' }) => (
    <div className={`${bg} rounded-xl p-5`}>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {samples.map(s => <Card key={s.id} c={s} />)}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 p-8">
      <div className="max-w-6xl mx-auto">
        <header className="mb-10">
          <h1 className="text-3xl font-bold text-slate-900">Cards da Agenda — 3 Propostas Sofisticadas</h1>
          <p className="text-slate-500 mt-2">Mais clean, com tipografia editorial, sombras suaves e identidade premium.</p>
        </header>

        <section className="mb-10 bg-white rounded-2xl p-6 shadow-sm">
          <SectionTitle
            n="A"
            name="Editorial / Serif Time"
            desc="Hora em fonte serifada (estilo revista), microbar com cor + label uppercase espaçada (letter-spacing 0.18em), divisória sutil no rodapé. Inspirado em Calendly Pro e produtos de design editorial."
          />
          <Grid Card={CardA} bg="bg-stone-50" />
        </section>

        <section className="mb-10 bg-white rounded-2xl p-6 shadow-sm">
          <SectionTitle
            n="B"
            name="Premium Glass / Linear-style"
            desc="Gradiente diagonal sutil, faixa vertical colorida à esquerda, badges de status com ring (anel duplo). Inspirado em Linear, Notion e dashboards SaaS premium."
          />
          <Grid Card={CardB} bg="bg-slate-50" />
        </section>

        <section className="mb-10 bg-white rounded-2xl p-6 shadow-sm">
          <SectionTitle
            n="C"
            name="Boutique / Pastel Capsule"
            desc="Cards com aura colorida pastel (ring-1), pílulas brancas semi-transparentes com backdrop-blur, badge financeiro flutuante no canto superior direito. Inspirado em apps de luxo, spa e fashion-tech."
          />
          <Grid Card={CardC} bg="bg-amber-50/30" />
        </section>

        <footer className="text-center text-sm text-slate-400 mt-12 pb-6">
          Todos os cards exibem: paciente, horário, procedimento, modo de atendimento, retorno, preventivo, status e valor pago.<br />
          Aplicarão em AgendaPage (dia/semana/mês), DashboardPage, ConsultaDetalhesPage e qualquer outro lugar onde consultas são listadas.
        </footer>
      </div>
    </div>
  );
}
